import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * EditSessionDialog - Dialog for editing an existing session
 */
export function EditSessionDialog({
  open,
  onOpenChange,
  formData,
  setFormData,
  locations,
  onSubmit
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">EDIT SESSION</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Session Title (Optional)</Label>
            <Input
              type="text"
              value={formData.title || ''}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., STAY ON x Nike Session"
              className="mt-1"
              data-testid="edit-session-title"
            />
            <p className="text-xs text-muted-foreground mt-1">Add a custom title for partner sessions</p>
          </div>
          
          <div>
            <Label>Location</Label>
            <Select 
              value={formData.location_id} 
              onValueChange={(v) => setFormData({...formData, location_id: v})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              required
              className="mt-1"
              data-testid="edit-session-date"
            />
            <p className="text-xs text-muted-foreground mt-1">All times in Pacific Time (PST/PDT)</p>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start Time</Label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                required
                className="mt-1"
                data-testid="edit-start-time"
              />
            </div>
            <div>
              <Label>End Time</Label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                required
                className="mt-1"
                data-testid="edit-end-time"
              />
            </div>
          </div>
          
          <div>
            <Label>Court/Area (Optional)</Label>
            <Input
              value={formData.court}
              onChange={(e) => setFormData({...formData, court: e.target.value})}
              className="mt-1"
              placeholder="e.g., Court A, Main Gym"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Capacity</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={formData.capacity}
                onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value) || 25})}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Price</Label>
              <Select
                value={String(formData.price)}
                onValueChange={(value) => setFormData({...formData, price: parseFloat(value)})}
              >
                <SelectTrigger className="mt-1" data-testid="edit-price-select">
                  <SelectValue placeholder="Select price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Free ($0)</SelectItem>
                  <SelectItem value="10">$10</SelectItem>
                  <SelectItem value="15">$15</SelectItem>
                  <SelectItem value="20">$20</SelectItem>
                  <SelectItem value="25">$25</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label>Grade Level</Label>
            <Select
              value={formData.grade_group || formData.age_group}
              onValueChange={(value) => setFormData({...formData, grade_group: value})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select grade level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="elementary">Elementary (3rd-5th Grade)</SelectItem>
                <SelectItem value="middle">Middle School (6th-8th Grade)</SelectItem>
                <SelectItem value="high">High School (9th-12th Grade)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1 btn-primary" data-testid="save-edit-session-btn">
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
